import { useCommentStore } from "../stores/commentStore";
import { useProjectStore } from "../stores/projectStore";
import { MIN_WIDTH_WHEN_HIDING_COMMENTS, MIN_WIDTH_WHEN_SHOWING_COMMENTS } from "../../../src/shared/constants";
import { commentsManager } from "../components/ShareLinkPage/CommentsManager";

let rightBarCollapsedByComments = false;
let leftBarCollapsedByComments = false;

// Shared toggle logic that can be used by both hook and HOC
// `options.userInitiated` defaults to `true`. When `false` (e.g. the auto-open
// effect on document mount, or an automatic close on document switch) the
// per-document "user closed it" persistence in projectStore is *not* updated —
// only an explicit user action should change Dean's sticky-close flag.
export const toggleCommentsLogic = (newShowCommentState, stores, options = {}) => {
  const { setShowComments, columnWidths, setRightBarCollapsed, setLeftBarCollapsed, setColumnWidths } = stores;
  const userInitiated = options.userInitiated !== false;

  setShowComments(newShowCommentState);

  // SW-8335 — Dean's bug fix: persist "user closed the sidebar" per-document so
  // that the auto-open effect in EditorContainer respects the close on reload.
  // Read documentId from commentStore (kept in sync by EditorContainer on mount)
  // and update the persisted flag on projectStore. Only user-initiated toggles
  // change this — automatic toggles on document load/switch must not mark the
  // doc as "user-closed".
  if (userInitiated) {
    try {
      const activeDocumentId = useCommentStore.getState()?.documentId;
      if (activeDocumentId) {
        const projectState = useProjectStore.getState();
        if (newShowCommentState) {
          projectState.clearCommentSidebarUserClosed?.(activeDocumentId);
        } else {
          projectState.markCommentSidebarUserClosed?.(activeDocumentId);
        }
      }
    } catch {
      // Defensive: never let persistence bookkeeping break the user-visible toggle.
    }
  }

  if (newShowCommentState) {
    // collapse the right or left bar if the center column is too narrow for comments
    const currentCenterWidth = columnWidths.center.current;
    if (currentCenterWidth < MIN_WIDTH_WHEN_SHOWING_COMMENTS) {
      if (!columnWidths.right.collapsed) {
        rightBarCollapsedByComments = true;
        setRightBarCollapsed(true);
      } else if (!columnWidths.left.collapsed) {
        leftBarCollapsedByComments = true;
        setLeftBarCollapsed(true);
      }
    }

    setColumnWidths((draft) => {
      draft.center.min = MIN_WIDTH_WHEN_SHOWING_COMMENTS;
    });
  } else {
    setColumnWidths((draft) => {
      draft.center.min = MIN_WIDTH_WHEN_HIDING_COMMENTS;
    });

    if (rightBarCollapsedByComments) {
      const project = useProjectStore.getState().project;
      const importWip = project?.importStep && project.importStep !== "CANCELLED";
      if (!importWip) {
        setRightBarCollapsed(false);
      }
      rightBarCollapsedByComments = false;
    }
    if (leftBarCollapsedByComments) {
      setLeftBarCollapsed(false);
      leftBarCollapsedByComments = false;
    }

    commentsManager.totalCommentsHeight = 0;

    // Properly notify any registered listeners about the height change
    if (typeof commentsManager.notifyHeightChange === "function") {
      commentsManager.notifyHeightChange();
    }
  }

  // Trigger a resize event to make sure the editor layout updates
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
  }, 10);
};

export const useToggleCommentMode = () => {
  const showComments = useCommentStore((state) => state.showComments);
  const setShowComments = useCommentStore((state) => state.setShowComments);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const setRightBarCollapsed = useProjectStore((state) => state.setRightBarCollapsed);
  const setLeftBarCollapsed = useProjectStore((state) => state.setLeftBarCollapsed);
  const setColumnWidths = useProjectStore((state) => state.setColumnWidths);

  const toggleComments = (newShowCommentState, options) => {
    toggleCommentsLogic(newShowCommentState, {
      setShowComments,
      columnWidths,
      setRightBarCollapsed,
      setLeftBarCollapsed,
      setColumnWidths
    }, options);
  };

  return {
    showComments,
    toggleComments,
  };
};
